import { useTranslation } from 'react-i18next';
import { styled } from 'goober';

import type { SearchSuggestion } from '@/api/v4/search.api';
import { Loader } from '@/components/loader/loader';
import { getCategoryIcon } from '@/features/modals/manage-tags-modal/helpers/get-category-icon';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { colorIcon } from '@/ui/icons/functions';
import { Icon } from '@/ui/icons/icon';
import { GeneralTag } from '@/ui/tag/general-tag';
import { Small } from '@/ui/typography/across-platform';

import { SuggestionContainer } from './suggestion-container';
import { useCountEntitiesByTag } from './use-count-entities-by-tag.query';
import { Link } from '@/ui/link/link';
import { paths } from '@/routes/helpers/paths';
import { parseSearchParams } from '@/routes/helpers/parse-search-params';
import { modalState } from '@/state/modal.state';
import { useSetAtom } from 'jotai';

export const TagSuggestion = ({
  suggestion,
}: {
  suggestion: SearchSuggestion;
}) => {
  return (
    <SuggestionContainer label={suggestion.name}>
      <Flexbox name="tag-suggestion-content" justify="space-between" fullWidth>
        <GeneralTag variant="blue">
          {suggestion.name}{' '}
          <Icon icon={getCategoryIcon(suggestion.category ?? '')} />
        </GeneralTag>

        <TagEntitiesCounts tagId={suggestion.id} />
      </Flexbox>
    </SuggestionContainer>
  );
};

const TagEntitiesCounts = ({ tagId }: { tagId: string }) => {
  const { t } = useTranslation('default', {
    keyPrefix: 'globalSearch',
  });

  const { data, isLoading } = useCountEntitiesByTag(tagId);
  const setModal = useSetAtom(modalState);

  if (!data || isLoading) return <Loader size="small" />;

  return (
    <Flexbox name="count-entities-by-tag-container" gap="12px">
      <Link
        to={
          paths.companies() +
          parseSearchParams({
            tags: tagId,
          })
        }
        onClick={() => setModal(null)}
      >
        <Entity count={data.organizations} label={t`companies`} />
      </Link>
      <Link
        to={
          paths.projects() +
          parseSearchParams({
            tags: tagId,
          })
        }
        onClick={() => setModal(null)}
      >
        <Entity count={data.projects} label={t`projects`} />
      </Link>
    </Flexbox>
  );
};

const Entity = ({ label, count }: { label: string; count: number }) => {
  return (
    <Flexbox name="entity-link" gap="6px">
      <EntityCount>({count}) </EntityCount>
      <EntityText>
        {label} &nbsp; <Icon icon="ArrowRight" />
      </EntityText>
    </Flexbox>
  );
};

const EntityCount = styled(Small)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.gray.c12};
`;

const EntityText = styled(Small)`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.blue.primaryA};

  ${({ theme }) => colorIcon(theme.colors.blue.primaryA)}

  &:hover {
    text-decoration: underline;
  }
`;
